import type { Service } from '../../typings/common/booking';

/**
 * Minutes in a day. Services with a block size of a day or more are booked as
 * date ranges rather than as time slots.
 */
export const MINUTES_PER_DAY = 1440;

/**
 * The parts of a service that decide which durations it can be booked for.
 */
export type DurationService = Pick<
	Service,
	'blockSize' | 'flexibleDuration'
> & {
	minDuration?: number | null;
	maxDuration?: number | null;
};

/**
 * The durations a service offers, in minutes, shortest first.
 *
 * A service without flexible duration offers exactly one: its block size. A
 * flexible one offers every block-size step from its minimum to its maximum.
 *
 * @param service Service to read the duration settings from.
 *
 * @return Offered durations in minutes, or an empty array when the service has
 *         no usable block size.
 */
export function getDurationOptions(service: DurationService): number[] {
	const blockSize = service.blockSize ?? 0;

	if (blockSize <= 0) {
		return [];
	}

	if (!service.flexibleDuration) {
		return [blockSize];
	}

	const minDuration = service.minDuration ?? blockSize;
	const maxDuration = service.maxDuration ?? blockSize;
	const options: number[] = [];

	for (
		let duration = minDuration;
		duration <= maxDuration;
		duration += blockSize
	) {
		options.push(duration);
	}

	// A maximum below the minimum offers nothing to step through; the minimum
	// is still bookable.
	return options.length > 0 ? options : [minDuration];
}

/**
 * Snap a duration to one the service actually offers.
 *
 * A duration the service already offers is kept. Anything else falls back to
 * the service's shortest duration, which is what picking the service fresh
 * would give.
 *
 * @param service  Service to snap the duration to.
 * @param duration Duration in minutes to snap.
 *
 * @return A duration the service offers, or 0 when it offers none.
 */
export function normalizeDuration(
	service: DurationService,
	duration: number
): number {
	const blockSize = service.blockSize ?? 0;

	if (blockSize <= 0) {
		return 0;
	}

	if (!service.flexibleDuration) {
		return duration === blockSize ? duration : blockSize;
	}

	const minDuration = service.minDuration ?? blockSize;
	const isOffered =
		duration >= minDuration &&
		(service.maxDuration === undefined ||
			service.maxDuration === null ||
			duration <= service.maxDuration) &&
		duration % blockSize === 0;

	return isOffered ? duration : minDuration;
}
